import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Field,
  Page,
  PageHeader,
  Select,
  Table,
  backLinkClasses,
  type Column,
} from '@evogard/ui';
import {
  BILLING_AWARD_LINK_DEADLINE_DAYS,
  BILLING_AWARD_LINK_STATUSES,
  BILLING_AWARD_LINK_STATUS_LABELS,
  csvCell,
  csvContent,
  csvDecimal,
  format as formatMoney,
  formatDate,
  formatInstant,
  fromColumn,
  loadFailureMessage,
  type BillingAwardLinkStatus,
} from '@evogard/domain';
import type { BillingAwardLinkRow } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { saveCsv } from '../../../lib/csv.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { ReasonDialog } from './AwardDialogs.js';
import {
  AWARDS_KEY,
  currentPeriod,
  periodLabel,
  recentPeriods,
  useAwardTenants,
  useInvalidateAwards,
} from './useAwards.js';

const PAGE = 100;

// @valor
const TONE: Record<BillingAwardLinkStatus, 'sucesso' | 'informacao' | 'neutro' | 'erro'> = {
  retido: 'sucesso',
  monitorando: 'informacao',
  expirado: 'neutro',
  cancelado: 'erro',
};

/**
 * O monitoramento da premiação — os vínculos atendimento × boleto de uma
 * competência numa base, com a invalidação (o botão "Comissão" do legado).
 *
 * O "X / 8" é só informação: com o recálculo diário, o prazo de oito dias
 * recomeça todo dia no mês corrente, e só vence no mês que já virou.
 */
export function AwardsLinksScreen() {
  const tenants = useAwardTenants();
  const [slug, setSlug] = useState('');
  const tenant = tenants.find((t) => t.slug === slug) ?? tenants[0];
  const [period, setPeriod] = useState(currentPeriod());
  const [status, setStatus] = useState<BillingAwardLinkStatus | ''>('');
  const [search, setSearch] = useState('');
  const [pages, setPages] = useState(1);
  const [invalidating, setInvalidating] = useState<BillingAwardLinkRow | null>(null);
  const q = useDebouncedTerm(search.trim());
  const invalidate = useInvalidateAwards();

  const query = useQuery({
    enabled: tenant !== undefined,
    queryKey: [...AWARDS_KEY, 'vinculos', tenant?.slug, period, status, q, pages],
    queryFn: async () => {
      const items: BillingAwardLinkRow[] = [];
      let cursor: string | undefined;
      let next: string | null = null;
      for (let i = 0; i < pages; i++) {
        const r = await api.billingAwards.links({
          params: { tenant: tenant!.slug },
          query: { period, status: status || undefined, q: q.length >= 2 ? q : undefined, cursor, limit: PAGE },
        });
        if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os vínculos'));
        items.push(...r.body.items);
        next = r.body.next;
        if (next === null) break;
        cursor = next;
      }
      return { items, next };
    },
  });
  const view = useQuery({
    enabled: tenant !== undefined,
    queryKey: [...AWARDS_KEY, 'competencia', tenant?.slug, period],
    queryFn: async () =>
      ok(await api.billingAwards.period({ params: { tenant: tenant!.slug }, query: { period } }), 200, 'Não foi possível carregar a competência.'),
  });
  const canInvalidate = (view.data?.can.invalidate ?? false) && view.data?.closing === null;

  const run = useMutation({
    mutationFn: async (act: { link: BillingAwardLinkRow; reason?: string }) => {
      const params = { tenant: tenant!.slug, id: act.link.id };
      return act.reason === undefined
        ? ok(await api.billingAwards.revalidate({ params, body: {} }), 200, 'Não foi possível revalidar.')
        : ok(await api.billingAwards.invalidate({ params, body: { reason: act.reason } }), 200, 'Não foi possível invalidar.');
    },
    onSuccess: () => {
      setInvalidating(null);
      invalidate();
    },
  });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Monitoramento da premiação"
        back={
          <Link to="/billing/awards" className={backLinkClasses()}>
            Premiação da cobrança
          </Link>
        }
      />
      <div className="flex flex-wrap items-end gap-3">
        {tenants.length > 1 && (
          <div className="w-44">
            <Select label="Base" value={tenant?.slug ?? ''} onChange={(e) => {
                setSlug(e.target.value);
                setPages(1);
              }}>
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="w-52">
          <Select label="Competência" value={period} onChange={(e) => {
              setPeriod(e.target.value);
              setPages(1);
            }}>
            {recentPeriods().map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-44">
          <Select
            label="Estado"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as BillingAwardLinkStatus | '');
              setPages(1);
            }}
          >
            <option value="">Todos</option>
            {BILLING_AWARD_LINK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {BILLING_AWARD_LINK_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-80">
          <Field
            label="Buscar"
            placeholder="Cliente, nosso número, placa, operador ou protocolo"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPages(1);
            }}
          />
        </div>
        <Button
          variant="contorno"
          disabled={!query.data || query.data.items.length === 0}
          onClick={() => query.data && downloadLinks(query.data.items, tenant!.slug, period)}
        >
          Exportar CSV
        </Button>
      </div>

      {query.error && <Alert tone="erro">{query.error.message}</Alert>}
      {view.data?.closing && (
        <Alert tone="informacao">
          Competência fechada nesta base: os vínculos estão congelados, e invalidar exige reabrir.
        </Alert>
      )}

      <Table<BillingAwardLinkRow>
        label="Vínculos da competência"
        empty="Nenhum vínculo com esse filtro. Sem atendimento do SGA na competência não há vínculo — veja a quantidade no painel."
        items={query.data?.items ?? []}
        rowKey={(l) => String(l.id)}
        loading={query.isLoading}
        paginate={false}
        columns={columns(canInvalidate, (link) => setInvalidating(link), (link) => run.mutate({ link }))}
      />
      {query.data?.next && (
        <div>
          <Button variant="contorno" loading={query.isFetching} onClick={() => setPages((p) => p + 1)}>
            Carregar mais
          </Button>
        </div>
      )}
      {run.error && !invalidating && <Alert tone="erro">{run.error.message}</Alert>}

      {invalidating && (
        <ReasonDialog
          title="Invalidar o vínculo"
          descricao={`${invalidating.memberName ?? 'Boleto'} · ${invalidating.operator.name}. Invalidado, o vínculo não conta para a premiação, e continua invalidado depois de cada recálculo.`}
          confirm="Invalidar"
          pending={run.isPending}
          error={run.error?.message ?? null}
          onConfirm={(reason) => run.mutate({ link: invalidating, reason })}
          onClose={() => setInvalidating(null)}
        />
      )}
    </Page>
  );
}

function elapsed(link: BillingAwardLinkRow): string {
  const days = Math.floor((Date.now() - new Date(link.matchedAt).getTime()) / 86_400_000);
  return `${Math.min(Math.max(days, 0), BILLING_AWARD_LINK_DEADLINE_DAYS)} / ${BILLING_AWARD_LINK_DEADLINE_DAYS}`;
}

function columns(
  canInvalidate: boolean,
  onInvalidate: (l: BillingAwardLinkRow) => void,
  onRevalidate: (l: BillingAwardLinkRow) => void,
): Array<Column<BillingAwardLinkRow>> {
  return [
    {
      title: 'Atendimento',
      cell: (l) => (
        <span className="flex flex-col whitespace-nowrap">
          <span>{l.record.servedOn ? formatDate(l.record.servedOn) : '—'}</span>
          <span className="text-xs text-texto-suave">nº {l.record.externalId}</span>
        </span>
      ),
    },
    {
      title: 'Cliente',
      alignment: 'inicio',
      cell: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="font-medium text-texto">{l.memberName ?? 'Sem nome'}</span>
          <span className="text-xs text-texto-suave">
            {[l.plate, l.boleto.nossoNumero ? `nosso nº ${l.boleto.nossoNumero}` : null, l.record.protocol ? `protocolo ${l.record.protocol}` : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      ),
    },
    { title: 'Operador', alignment: 'inicio', cell: (l) => l.operator.name },
    { title: 'Tipo', alignment: 'inicio', cell: (l) => <span className="text-sm">{l.boleto.typeLabel}</span> },
    {
      title: 'Boleto',
      alignment: 'fim',
      cell: (l) => (
        <span className="flex flex-col whitespace-nowrap">
          <span>{formatMoney(fromColumn(l.boleto.amount)!)}</span>
          <span className="text-xs text-texto-suave">vence {formatDate(l.boleto.dueDate)}</span>
        </span>
      ),
    },
    {
      title: 'Estado',
      cell: (l) => (
        <span className="flex flex-col items-center gap-1">
          <Badge tone={TONE[l.status]}>{BILLING_AWARD_LINK_STATUS_LABELS[l.status]}</Badge>
          {l.status === 'monitorando' && <span className="text-xs text-texto-suave">{elapsed(l)}</span>}
          {l.settledOn && <span className="text-xs text-texto-suave">pago {formatDate(l.settledOn)}</span>}
        </span>
      ),
    },
    {
      title: 'Recuperado',
      alignment: 'fim',
      cell: (l) => (l.recovered ? formatMoney(fromColumn(l.recovered)!) : <span className="text-texto-suave">—</span>),
    },
    {
      title: 'Premiação',
      cell: (l) =>
        l.invalidation ? (
          <span className="flex flex-col items-center gap-1">
            <Badge tone="alerta">Invalidado</Badge>
            <span className="max-w-56 text-xs text-texto-suave" title={l.invalidation.reason}>
              {l.invalidation.by ? `${l.invalidation.by}: ` : ''}
              {l.invalidation.reason}
            </span>
            {canInvalidate && (
              <Button size="sm" variant="discreto" onClick={() => onRevalidate(l)}>
                Revalidar
              </Button>
            )}
          </span>
        ) : (
          <span className="flex flex-col items-center gap-1">
            {l.counts ? <Badge tone="sucesso">Conta</Badge> : <span className="text-xs text-texto-suave">ainda não conta</span>}
            {canInvalidate && (
              <Button size="sm" variant="discreto" onClick={() => onInvalidate(l)}>
                Invalidar
              </Button>
            )}
          </span>
        ),
    },
    {
      title: 'Conferido',
      cell: (l) =>
        l.checkedAt ? <span className="text-xs">{formatInstant(l.checkedAt)}</span> : <span className="text-texto-suave">—</span>,
    },
  ];
}

function downloadLinks(items: readonly BillingAwardLinkRow[], tenant: string, period: string): void {
  const header = ['Atendimento', 'Data do atendimento', 'Cliente', 'Placa', 'Nosso número', 'Operador', 'Tipo', 'Vencimento', 'Valor', 'Estado', 'Pago em', 'Recuperado', 'Conta', 'Motivo da invalidação'];
  const rows = items.map((l) => [
    csvCell(l.record.externalId ?? ''),
    csvCell(l.record.servedOn ? formatDate(l.record.servedOn) : ''),
    csvCell(l.memberName ?? ''),
    csvCell(l.plate ?? ''),
    csvCell(l.boleto.nossoNumero ?? ''),
    csvCell(l.operator.name),
    csvCell(l.boleto.typeLabel),
    csvCell(formatDate(l.boleto.dueDate)),
    csvDecimal(l.boleto.amount),
    csvCell(BILLING_AWARD_LINK_STATUS_LABELS[l.status]),
    csvCell(l.settledOn ? formatDate(l.settledOn) : ''),
    l.recovered ? csvDecimal(l.recovered) : '',
    csvCell(l.counts ? 'sim' : 'não'),
    csvCell(l.invalidation?.reason ?? ''),
  ]);
  saveCsv(csvContent([header.map(csvCell), ...rows]), `premiacao-vinculos-${period}`, tenant);
}
